import { useEffect, useState } from 'react'

import type { FishStockLog, MortalityLog, WeightSampleLog } from '../types/production'
import { findOtherSelfLocations, type LotLocationContext } from '../utils/crossFarmLotHistory'
import { fishStockLogApi } from '../utils/fishStockLogApi'
import { lotApi } from '../utils/lotApi'
import { mortalityLogApi } from '../utils/mortalityLogApi'
import { listProductionUnitTableRows, type BiomassDataRow } from '../utils/productionUnitTable'
import { weightSampleLogApi } from '../utils/weightSampleLogApi'

export type CrossFarmLotHistorySupplement = {
  extraProductionRows: BiomassDataRow[]
  extraMortalityLogs: MortalityLog[]
  extraWeightSampleLogs: WeightSampleLog[]
  extraFishStockLogs: FishStockLog[]
}

const EMPTY_SUPPLEMENT: CrossFarmLotHistorySupplement = {
  extraProductionRows: [],
  extraMortalityLogs: [],
  extraWeightSampleLogs: [],
  extraFishStockLogs: []
}

const normalizeLotIds = (lotIds: readonly string[]): string[] =>
  [...new Set(lotIds.map((id) => id.trim()).filter(Boolean))].sort()

const fetchSupplementForLocations = async (
  locations: readonly LotLocationContext[]
): Promise<CrossFarmLotHistorySupplement> => {
  const fetched = await Promise.all(locations.map(async (location) => {
    const [rows, mortality, weight, stock] = await Promise.all([
      listProductionUnitTableRows(location).catch(() => [] as BiomassDataRow[]),
      mortalityLogApi.list(location, { preferLocal: true }).catch(() => [] as MortalityLog[]),
      weightSampleLogApi.list(location, { preferLocal: true }).catch(() => [] as WeightSampleLog[]),
      fishStockLogApi.list(location, { preferLocal: true }).catch(() => [] as FishStockLog[])
    ])
    return { rows, mortality, weight, stock }
  }))

  return {
    extraProductionRows: fetched.flatMap((entry) => entry.rows),
    extraMortalityLogs: fetched.flatMap((entry) => entry.mortality),
    extraWeightSampleLogs: fetched.flatMap((entry) => entry.weight),
    extraFishStockLogs: fetched.flatMap((entry) => entry.stock)
  }
}

/**
 * See crossFarmLotHistory.ts for the problem this solves. Fetches a
 * *targeted* supplement -- only the lot(s) actually in view, only their
 * *other* locations -- rather than expanding any screen's primary,
 * farm/production-unit-scoped data load. In the overwhelmingly common case
 * (no lot in view has ever moved), this does exactly one network call
 * (the lineage lookup) and no extra data fetches at all.
 *
 * Deliberately kept separate from a screen's primary `productionRows`/log
 * state rather than merged into it -- those also drive editable grids, and
 * silently mixing in another farm's rows there would be a correctness bug
 * for the *editing* surface, not just the biomass computation. Callers
 * concatenate this supplement in only at the specific continuity
 * computation (e.g. `computeCurrentBiomassPerLot`), not into what's
 * rendered/editable.
 */
export const useCrossFarmLotHistorySupplement = (options: {
  farmId?: string
  productionUnitId?: string
  lotIds: readonly string[]
  enabled?: boolean
}): CrossFarmLotHistorySupplement => {
  const { farmId, productionUnitId, enabled = true } = options
  const lotIdsKey = normalizeLotIds(options.lotIds).join(',')
  const [supplement, setSupplement] = useState<CrossFarmLotHistorySupplement>(EMPTY_SUPPLEMENT)

  useEffect(() => {
    if (!enabled || !farmId || !productionUnitId || !lotIdsKey) {
      setSupplement(EMPTY_SUPPLEMENT)
      return
    }

    let cancelled = false

    void (async () => {
      const lineageRows = await lotApi.lineageForLots(lotIdsKey.split(','))
      if (cancelled) return

      const otherLocations = findOtherSelfLocations(lineageRows, { farmId, productionUnitId })
      if (otherLocations.length === 0) {
        setSupplement(EMPTY_SUPPLEMENT)
        return
      }

      const result = await fetchSupplementForLocations(otherLocations)
      if (!cancelled) setSupplement(result)
    })()

    return () => {
      cancelled = true
    }
  }, [enabled, farmId, productionUnitId, lotIdsKey])

  return supplement
}
